import type { Reference } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import {
  ExtendedMedicationRequest,
  formatPrescriptionLabel,
  GRANERX_DISCONTINUATION_ACTOR,
  isGraneRxDiscontinuationActor,
  type PrescriptionLabel,
  prescriptionLabels,
} from '@seen/core/models/medicationRequest'
import { getReferenceString } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import { Badge, cn } from '@seen/ui'
import { IconDots } from '@tabler/icons-react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { orderBy, uniqBy } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import { useVersionOriginTask } from '#src/components/activity/useVersionOriginTask.ts'
import {
  authoredOnTooltipDetail,
  orderEventTimestamp,
  statusSetAt,
  stopEventTimestamp,
} from '#src/components/medications/medicationActivityDates.ts'
import { MedicationHoverCardHistoryEntry } from '#src/components/medications/MedicationHoverCardHistoryEntry.tsx'
import {
  type PharmacyTransmissionEvent,
  PharmacyTransmissionStatusIcon,
} from '#src/components/medications/PharmacyTransmissionStatusIcon.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useBulkResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

const HISTORY_PREVIEW_COUNT = 3

export interface MedicationActivityProps {
  medicationRequest: ExtendedMedicationRequest
  groupedMedications?: ExtendedMedicationRequest[]
  /* IDs of MedicationRequests to visually highlight (e.g. encounter-linked entries) */
  highlightedRequestIds?: ReadonlySet<string>
  collapsible?: boolean
}

export function MedicationActivity({
  medicationRequest,
  groupedMedications,
  highlightedRequestIds,
  collapsible,
}: MedicationActivityProps): JSX.Element {
  const groupMembers =
    groupedMedications && groupedMedications.length > 0
      ? groupedMedications
      : [medicationRequest]

  const medicationIds = Array.from(
    new Set(groupMembers.map((m) => m.id).filter((id): id is string => !!id))
  )

  // Every group member's reference, so comments left on any request in the
  // thread fold into one timeline rather than only those on the request whose
  // detail page is open.
  const groupReferences = groupMembers.map((m) => m.reference)
  const groupReferenceStrings = new Set(groupReferences.map(getReferenceString))

  const { data: historyData = [] } = useBulkResourceVersions(
    ExtendedMedicationRequest,
    medicationIds
  )
  const medicationOriginOf = useVersionOriginTask(historyData)

  const allVersions = useMemo(() => {
    return orderBy(
      uniqBy(historyData, (v) => `${v.id}-${v.versionId}`),
      (version) => version.authoredOn ?? version.lastUpdated ?? '',
      ['desc']
    )
  }, [historyData])

  const showPharmacyStopAttribution = useFeatureFlag(
    featureFlags.medicationPharmacyStopAttribution
  )

  const byPrescription = useFeatureFlag(
    featureFlags.medicationHistoryByPrescription
  )
  // One prescription needs no label: every line is about it.
  const labels =
    byPrescription && medicationIds.length > 1
      ? prescriptionLabels(groupMembers)
      : undefined

  const sourceSystem = getMedicationSourceSystem(allVersions)

  const medicationActivity = generateVersionHistoryEventsFromAll(
    allVersions,
    sourceSystem,
    showPharmacyStopAttribution
  )

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery(groupReferences)
  )

  const all = orderBy(
    [...medicationActivity, ...comments],
    (a) => {
      if (a instanceof CommentCommunication) {
        return a.sent
      }
      /* Use timestamp for system events (e.g., fulfillment time_completed), then effectiveDate, then lastUpdated */
      return (
        a.timestamp ??
        a.medicationRequest.effectiveDate ??
        a.medicationRequest.lastUpdated
      )
    },
    ['desc']
  )

  const patientId = isOfType(medicationRequest.subject, 'Patient')
    ? (medicationRequest.subject.reference?.split('/')[1] ?? '')
    : ''

  /* Find the entry to highlight for each encounter-linked med.
   * Prefer the "initial" (ordered) event since that's the actual prescribing act.
   * Fall back to the oldest entry for that ID if no initial event exists. */
  const highlightKeys = useMemo(() => {
    if (!highlightedRequestIds?.size) {
      return new Set<string>()
    }
    const keys = new Set<string>()
    const candidatesByMedId = new Map<
      string,
      { key: string; isInitial: boolean }[]
    >()
    for (const entry of all) {
      if (entry instanceof CommentCommunication) {
        continue
      }
      const medId = entry.medicationRequest.id
      if (!medId || !highlightedRequestIds.has(medId)) {
        continue
      }
      const entryKey = entry.key ?? entry.medicationRequest.versionId ?? ''
      const isInitial = entry.eventType === 'initial'
      const existing = candidatesByMedId.get(medId) ?? []
      existing.push({ key: entryKey, isInitial })
      candidatesByMedId.set(medId, existing)
    }
    for (const [, candidates] of candidatesByMedId) {
      const initial = candidates.find((c) => c.isInitial)
      /* Prefer initial event, fall back to last (oldest) entry */
      const chosen = initial ?? candidates[candidates.length - 1]
      if (chosen) {
        keys.add(chosen.key)
      }
    }
    return keys
  }, [all, highlightedRequestIds])

  const [historyExpanded, setHistoryExpanded] = useState(false)
  const previewEntries = [
    ...all.slice(0, HISTORY_PREVIEW_COUNT),
    ...all.slice(HISTORY_PREVIEW_COUNT).filter((entry) => {
      if (entry instanceof CommentCommunication) {
        return false
      }
      return (
        highlightKeys.has(
          entry.key ?? entry.medicationRequest.versionId ?? ''
        ) ||
        (!!labels && entry.medicationRequest.id === medicationRequest.id)
      )
    }),
  ]
  const canCollapse = !!collapsible && previewEntries.length < all.length
  const visibleEntries = canCollapse && !historyExpanded ? previewEntries : all

  const entryNodes = visibleEntries.map((entry) => {
    if (entry instanceof CommentCommunication) {
      return (
        <ActivityComment
          key={entry.reference.reference}
          comment={entry}
          showRelated={
            !entry.partOf
              ?.map(getReferenceString)
              .some((r) => groupReferenceStrings.has(r))
          }
        />
      )
    }
    /*
     * Use the timestamp that was calculated during event generation.
     * This timestamp is already set to the correct value based on event type:
     * - stopped/cancelled/completed: first version where status appeared
     * - ordered/documented/renewed: effectiveDate (startDate ?? authoredOn)
     * - system events: explicit timestamp (e.g., fulfillment time_completed)
     * - default: lastUpdated
     */
    const time = entry.timestamp ?? entry.medicationRequest.lastUpdated
    /*
     * When the displayed date is the clinical start date rather than the
     * day the record was authored (a backdated documented med), surface the
     * keyed-in authoredOn on hover so the audit trail is not lost.
     */
    const timeTooltipDetail = authoredOnTooltipDetail(
      entry.eventType,
      entry.medicationRequest
    )
    const resolvedAuthor = resolveMedicationActivityAuthor(
      entry,
      allVersions,
      showPharmacyStopAttribution
    )

    const entryKey = entry.key ?? entry.medicationRequest.versionId ?? ''
    const isHighlighted = highlightKeys.has(entryKey)
    const isCurrentMedication =
      entry.medicationRequest.id === medicationRequest.id
    const prescriptionLabel = entry.medicationRequest.id
      ? labels?.get(entry.medicationRequest.id)
      : undefined
    const label = prescriptionLabel && (
      <PrescriptionLabelLine
        label={prescriptionLabel}
        isViewed={isCurrentMedication}
      />
    )
    const isDimmed = !!prescriptionLabel?.isClosed && !isCurrentMedication

    const action = (
      <span
        className={
          collapsible
            ? '[&_a]:font-normal [&_a]:text-inherit'
            : 'ml-1 inline-flex items-center gap-1'
        }
      >
        {entry.actionBuilder
          ? entry.actionBuilder(entry.medicationRequest, patientId)
          : entry.action}
        {entry.transmissionEvents && entry.transmissionEvents.length > 0 && (
          <PharmacyTransmissionStatusIcon events={entry.transmissionEvents} />
        )}
      </span>
    )
    const nestedEvents = nestedEventsForEntry(entry)
    const statusReason = entry.statusReason ? (
      <div className="text-xs text-muted-foreground">{entry.statusReason}</div>
    ) : undefined

    return (
      <div
        key={entry.key ?? entry.medicationRequest.versionId}
        data-prescription-label={prescriptionLabel?.number}
        className={cn(isDimmed && 'opacity-60')}
      >
        {collapsible ? (
          <div
            className={cn(isHighlighted && 'rounded-sm bg-accent px-1 py-px')}
          >
            <MedicationHoverCardHistoryEntry
              label={label}
              author={resolvedAuthor}
              time={time}
              timeFormat={getTimeFormat(time)}
              timeTooltipDetail={timeTooltipDetail}
              action={action}
              originTask={medicationOriginOf(entry.medicationRequest)}
              nestedEvents={nestedEvents}
            >
              {statusReason}
            </MedicationHoverCardHistoryEntry>
            {isHighlighted && (
              <div className="ml-5 pb-0.5 text-[10px] font-medium text-muted-foreground">
                Prescribed during this visit
              </div>
            )}
          </div>
        ) : (
          <div
            className={cn(
              isHighlighted &&
                '-mx-1 rounded-sm bg-accent px-1 py-px [&_.z-10]:bg-accent'
            )}
          >
            <ActivityEntry
              label={label}
              author={resolvedAuthor}
              time={time}
              timeFormat={getTimeFormat(time)}
              timeTooltipDetail={timeTooltipDetail}
              originTask={medicationOriginOf(entry.medicationRequest)}
              action={action}
              nestedEvents={nestedEvents}
              className={
                isCurrentMedication && !labels
                  ? '-mx-2 rounded-sm bg-accent px-2'
                  : undefined
              }
            >
              {statusReason}
            </ActivityEntry>
            {isHighlighted && (
              <div className="ml-9.5 pb-0.5 text-[10px] font-medium text-muted-foreground">
                Prescribed during this visit
              </div>
            )}
          </div>
        )}
      </div>
    )
  })

  return (
    <>
      {collapsible ? (
        <div className="flex flex-col gap-2">{entryNodes}</div>
      ) : (
        entryNodes
      )}
      {canCollapse && (
        <button
          type="button"
          onClick={() => setHistoryExpanded((expanded) => !expanded)}
          className="flex items-center gap-1 text-xs text-muted-foreground"
        >
          <IconDots className="size-3" />
          {historyExpanded ? 'Less' : 'More'}
        </button>
      )}
    </>
  )
}

function PrescriptionLabelLine({
  label,
  isViewed,
}: {
  label: PrescriptionLabel
  isViewed: boolean
}): JSX.Element {
  return (
    <div
      data-testid="prescription-label"
      className="flex flex-wrap items-center gap-1.5 text-[10px] leading-4 font-medium text-muted-foreground"
    >
      <span>{formatPrescriptionLabel(label)}</span>
      {isViewed && (
        <Badge
          variant="outline"
          className="h-4 px-1.5 py-0 text-[10px] leading-none"
        >
          This prescription
        </Badge>
      )}
    </div>
  )
}

/* ============================================================================
 * Utility Functions
 * ============================================================================ */

/*
 * Check if a timestamp should be displayed with time or just the date.
 * Returns 'date' if the timestamp is date-only (no time component) or midnight,
 * otherwise returns 'datetime' to show the full timestamp.
 */
function getTimeFormat(timestamp: string | undefined): 'date' | 'datetime' {
  if (!timestamp) {
    return 'datetime'
  }

  /* Date-only string (YYYY-MM-DD) - no time component */
  if (!timestamp.includes('T')) {
    return 'date'
  }

  /* Check if time is midnight (00:00:00) */
  try {
    const parsed = parseISO(timestamp)
    if (
      parsed.getHours() === 0 &&
      parsed.getMinutes() === 0 &&
      parsed.getSeconds() === 0
    ) {
      return 'date'
    }
  } catch {
    /* Invalid date, default to datetime */
  }

  return 'datetime'
}

/*
 * Determine the source system for a medication request by checking the
 * first (oldest) version in the history.
 *
 * DoseSpot branch retained for legacy MedicationRequests from the discontinued
 * DoseSpot pilot — new records never carry this identifier.
 */
function getMedicationSourceSystem(
  allVersions: ExtendedMedicationRequest[]
): 'DoseSpot' | 'Elation' | 'GraneRx' | undefined {
  if (allVersions.length === 0) {
    return undefined
  }

  /* Get the oldest version (first entry chronologically) */
  const oldestVersion = allVersions[allVersions.length - 1]

  /* Check for Elation identifier */
  if (oldestVersion.elationId) {
    return 'Elation'
  }

  /* Check for DoseSpot identifier */
  const hasDoseSpotIdentifier = oldestVersion
    .identifiers()
    .some((i) => i.system?.includes('dosespot'))
  if (hasDoseSpotIdentifier) {
    return 'DoseSpot'
  }

  /* Check for GraneRx pharmacy reception on any version */
  const hasGraneRx = allVersions.some(
    (v) =>
      v.pharmacyReception != null ||
      v.pharmacyTransmission?.source === 'granerx'
  )
  if (hasGraneRx) {
    return 'GraneRx'
  }

  return undefined
}

type MedicationEventType =
  | 'initial'
  | 'renewed'
  | 'restarted'
  | 'dose-change'
  | 'stopped'
  | 'cancelled'
  | 'completed'
  | 'status-change'
  | 'other'

const EVENT_LABEL_MAP: Record<MedicationEventType, string> = {
  initial: 'prescribed',
  renewed: 'prescribed',
  restarted: 'restarted',
  'dose-change': 'changed dose',
  stopped: 'stopped',
  cancelled: 'cancelled',
  completed: 'recorded treatment with',
  'status-change': 'status changed',
  other: 'updated',
}

function terminalEventType(
  status: ExtendedMedicationRequest['status']
): MedicationEventType | undefined {
  if (
    status === 'stopped' ||
    status === 'cancelled' ||
    status === 'completed'
  ) {
    return status
  }
  return undefined
}

function isTerminalEventType(eventType: MedicationEventType | undefined) {
  return (
    eventType === 'stopped' ||
    eventType === 'cancelled' ||
    eventType === 'completed'
  )
}

/*
 * The sync stores an order as completed while its Elation thread is not
 * permanent, and as active once the thread is. A completed status held since
 * the first version is that mapping, not a clinical completion, however many
 * versions carry it.
 */
function isCompletedSinceFirstVersion(
  mr: ExtendedMedicationRequest,
  allRequests: ExtendedMedicationRequest[],
  index: number
): boolean {
  return (
    mr.status === 'completed' &&
    allRequests
      .slice(index + 1)
      .every((req) => req.id !== mr.id || req.status === 'completed')
  )
}

/* A prescription's first stored version is its sign event whatever its status. */
function determineEventType(
  mr: ExtendedMedicationRequest,
  allRequests: ExtendedMedicationRequest[],
  index: number
): MedicationEventType {
  const isOldestOfThisMed = !allRequests
    .slice(index + 1)
    .some((req) => req.id === mr.id)

  if (isOldestOfThisMed) {
    return 'initial'
  }

  const terminal = terminalEventType(mr.status)
  if (terminal) {
    return terminal
  }

  /* Check if dose changed */
  const nextOlder = allRequests[index + 1]
  if (nextOlder) {
    const currentDose = extractDoseFromDisplay(mr.medicationDisplay ?? '')
    const previousDose = extractDoseFromDisplay(
      nextOlder.medicationDisplay ?? ''
    )

    if (currentDose !== previousDose && currentDose && previousDose) {
      return 'dose-change'
    }
  }

  /* Check if status changed from previous version */
  if (
    nextOlder &&
    mr.status !== nextOlder.status &&
    nextOlder.status !== 'draft'
  ) {
    return 'status-change'
  }

  return 'renewed'
}

function extractDoseFromDisplay(display: string): string | null {
  const match = display.match(/(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|unit)/i)
  return match ? match[0] : null
}

function buildMedicationActionText(
  eventType: MedicationEventType,
  medicationRequest: ExtendedMedicationRequest,
  patientId: string,
  sourceSystem: 'DoseSpot' | 'Elation' | 'GraneRx' | undefined,
  showPharmacyStopAttribution: boolean
): JSX.Element {
  const isDocumented = medicationRequest.isDocumented
  const baseActionVerb = EVENT_LABEL_MAP[eventType]
  const isOrder =
    eventType === 'initial' ||
    eventType === 'renewed' ||
    eventType === 'restarted'

  /*
   * DISPLAY LOGIC: "prescribed" vs "documented" vs "refilled"
   *
   * Distinguish between different types of medication entries:
   *
   * 1. PRESCRIBED: Medications that were prescribed and sent to pharmacy
   *    - Has is_doc_med: false in externalResourceJSON
   *    - Has requester (prescribing physician)
   *    - May have pharmacy fulfillment information
   *    - Display: "Samantha Ho prescribed Lantus SoloStar... from Elation (28 units)"
   *
   * 2. DOCUMENTED: Medications that patient is taking but not prescribed by us
   *    - Has is_doc_med: true in externalResourceJSON
   *    - Has documenting_personnel instead of prescribing_physician
   *    - No pharmacy fulfillment (not sent to pharmacy)
   *    - Display: "documented by Julie Nguyen on 11/05/2025"
   *    - Typically appears as "Doc'd New" badge in Elation UI
   *
   * 3. REFILLED: Use explicit orderType from Elation when available
   *    - orderType="Refill" → "refilled"
   *    - orderType="New" → "prescribed"
   *    - orderType="DoseChange" → "changed dose"
   *
   * This distinction matches Elation's UI where documented meds show "Doc'd New"
   * and orders show "eRx" or pharmacy transmission details.
   */
  let actionVerb = baseActionVerb

  if (
    showPharmacyStopAttribution &&
    medicationRequest.isPharmacyOnlyDiscontinuation &&
    eventType === 'stopped'
  ) {
    actionVerb = 'initiated discontinuation of'
  } else if (isDocumented && isOrder) {
    actionVerb = 'documented'
  } else if (medicationRequest.orderType && isOrder) {
    /* Use explicit orderType from Elation when available */
    switch (medicationRequest.orderType) {
      case 'Refill':
        actionVerb = 'refilled'
        break
      case 'New':
        actionVerb = 'prescribed'
        break
      case 'DoseChange':
        actionVerb = 'changed dose'
        break
      default:
        /* Fall back to EVENT_LABEL_MAP for unknown orderTypes */
        actionVerb = baseActionVerb
    }
  }

  /* Use stable medication name from medicationCodeableConcept.text instead of
   * medicationDisplay which may have placeholder reference strings in older versions */
  const medicationName = medicationRequest.medicationName

  return (
    <span className="inline">
      {actionVerb}
      {medicationName && medicationRequest.id && (
        <>
          {' '}
          <a
            href={`/Patient/${patientId}/medications/${medicationRequest.id}`}
            className="font-medium text-foreground hover:underline"
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            {medicationName}
          </a>
        </>
      )}
      {eventType === 'completed' && ' completed'}
      {isOrder && sourceSystem && !isDocumented && (
        <>
          {' '}
          <span className="text-muted-foreground">from {sourceSystem}</span>
        </>
      )}
      {isOrder &&
        medicationRequest.dispenseRequest?.quantity &&
        !isDocumented && (
          <>
            {' '}
            <span className="text-muted-foreground">
              ({medicationRequest.dispenseRequest.quantity.value}{' '}
              {medicationRequest.dispenseRequest.quantity.unit || 'units'}
              {medicationRequest.dispenseRequest.expectedSupplyDuration && (
                <>
                  ,{' '}
                  {
                    medicationRequest.dispenseRequest.expectedSupplyDuration
                      .value
                  }
                  -day supply
                </>
              )}
              )
            </span>
          </>
        )}
    </span>
  )
}

/* Check if two medication requests have substantive differences */
/*
 * Check for substantive changes excluding status field.
 * Used to determine if status-change events should be shown.
 */
function hasSubstantiveChangesExcludingStatus(
  current: ExtendedMedicationRequest,
  previous: ExtendedMedicationRequest
): boolean {
  /* Dose changed */
  const currentDose = extractDoseFromDisplay(current.medicationDisplay ?? '')
  const previousDose = extractDoseFromDisplay(previous.medicationDisplay ?? '')
  if (currentDose !== previousDose) {
    return true
  }

  /* Dosage instructions changed */
  const currentSig = JSON.stringify(current.dosageInstructions ?? [])
  const previousSig = JSON.stringify(previous.dosageInstructions ?? [])
  if (currentSig !== previousSig) {
    return true
  }

  /* Quantity changed */
  const currentQty = current.dispenseRequest?.quantity?.value
  const previousQty = previous.dispenseRequest?.quantity?.value
  if (currentQty !== previousQty) {
    return true
  }

  /* Supply duration changed */
  const currentSupply = current.dispenseRequest?.expectedSupplyDuration?.value
  const previousSupply = previous.dispenseRequest?.expectedSupplyDuration?.value
  if (currentSupply !== previousSupply) {
    return true
  }

  /* Refills changed */
  const currentRefills = current.dispenseRequest?.numberOfRepeatsAllowed
  const previousRefills = previous.dispenseRequest?.numberOfRepeatsAllowed
  if (currentRefills !== previousRefills) {
    return true
  }

  return false
}

function hasSubstantiveChanges(
  current: ExtendedMedicationRequest,
  previous: ExtendedMedicationRequest
): boolean {
  /* Status changed */
  if (current.status !== previous.status) {
    return true
  }

  return hasSubstantiveChangesExcludingStatus(current, previous)
}

interface MedicationEvent {
  action: string
  medicationRequest: ExtendedMedicationRequest
  key?: string
  actionBuilder?: (
    mr: ExtendedMedicationRequest,
    patientId: string
  ) => JSX.Element
  statusReason?: string
  timestamp?: string
  eventType?: MedicationEventType
  nestedEvents?: {
    author?: Reference
    action: ReactNode
    time?: string
    children?: ReactNode
  }[]
  transmissionEvents?: PharmacyTransmissionEvent[]
}

function resolveMedicationActivityAuthor(
  event: MedicationEvent,
  allVersions: ExtendedMedicationRequest[],
  showPharmacyStopAttribution: boolean
): Reference | undefined {
  const medicationRequest = event.medicationRequest
  const isTerminal = isTerminalEventType(event.eventType)
  if (
    isTerminal &&
    showPharmacyStopAttribution &&
    medicationRequest.isPharmacyOnlyDiscontinuation
  ) {
    return medicationRequest.discontinuedBy ?? GRANERX_DISCONTINUATION_ACTOR
  }

  let author = medicationRequest.requester
  if (isTerminal) {
    const stoppedBy = medicationRequest.discontinuedBy
    const suppressPharmacyActor =
      !showPharmacyStopAttribution &&
      (isGraneRxDiscontinuationActor(stoppedBy) ||
        medicationRequest.isPharmacyOnlyDiscontinuation)
    author = suppressPharmacyActor ? undefined : stoppedBy
  }

  if (event.action === 'prescribed' || medicationRequest.isDocumented) {
    author = medicationRequest.signedBy ?? medicationRequest.requester ?? author
  }

  if (!author && medicationRequest.id) {
    const laterVersionWithAuthor = allVersions.find(
      (version) =>
        version.id === medicationRequest.id &&
        (version.signedBy || version.requester)
    )
    author =
      laterVersionWithAuthor?.signedBy ?? laterVersionWithAuthor?.requester
  }

  return author ?? medicationRequest.lastUpdatedBy
}

/* Generate comprehensive timeline from all version histories across the medication group */
function generateVersionHistoryEventsFromAll(
  allVersions: ExtendedMedicationRequest[],
  sourceSystem: 'DoseSpot' | 'Elation' | 'GraneRx' | undefined,
  showPharmacyStopAttribution: boolean
): MedicationEvent[] {
  const buildEvent = (
    curr: ExtendedMedicationRequest,
    eventType: MedicationEventType
  ): MedicationEvent => {
    const actionBuilder = (
      medReq: ExtendedMedicationRequest,
      patientId: string
    ): JSX.Element => {
      return buildMedicationActionText(
        eventType,
        medReq,
        patientId,
        sourceSystem,
        showPharmacyStopAttribution
      )
    }

    let eventTimestamp: string | undefined
    if (isTerminalEventType(eventType)) {
      eventTimestamp = stopEventTimestamp(curr, statusSetAt(curr, allVersions))
    } else if (
      eventType === 'initial' ||
      eventType === 'renewed' ||
      eventType === 'dose-change'
    ) {
      eventTimestamp = orderEventTimestamp(curr)
    } else {
      eventTimestamp = curr.lastUpdated
    }

    return {
      action: EVENT_LABEL_MAP[eventType],
      medicationRequest: curr,
      key: `${curr.id}-${curr.versionId}-${eventType}`,
      actionBuilder,
      eventType,
      statusReason: isTerminalEventType(eventType)
        ? curr.statusReason
        : undefined,
      timestamp: eventTimestamp,
    }
  }

  const events = allVersions.reduce<MedicationEvent[]>((acc, curr, idx) => {
    /* Determine event type based on comparison with next older version */
    const eventType = determineEventType(curr, allVersions, idx)
    const completedSinceFirstVersion = isCompletedSinceFirstVersion(
      curr,
      allVersions,
      idx
    )
    if (eventType !== 'initial' && completedSinceFirstVersion) {
      return acc
    }

    /*
     * A prescription first stored stopped or cancelled keeps its stop line next
     * to its sign line, even if a later version restores it.
     */
    const terminal = terminalEventType(curr.status)
    if (eventType === 'initial' && terminal && !completedSinceFirstVersion) {
      acc.push(buildEvent(curr, terminal))
    }

    /* Get the previous version for comparison */
    const prevVersion = allVersions[idx + 1]

    /* Always include: initial orders, dose changes, stopped/cancelled/completed, restarted */
    const isSignificantEvent =
      eventType === 'initial' ||
      isTerminalEventType(eventType) ||
      eventType === 'dose-change' ||
      eventType === 'restarted'

    /* For status-change events, only include if there are OTHER substantive changes */
    const isStatusChangeWithSubstantiveChanges =
      eventType === 'status-change' &&
      prevVersion &&
      hasSubstantiveChangesExcludingStatus(curr, prevVersion)

    /* For renewed events, only include if there are substantive changes */
    const isRenewalWithChanges =
      eventType === 'renewed' &&
      prevVersion &&
      hasSubstantiveChanges(curr, prevVersion)

    /* Include first version of each medication ID only if it's the first overall or has changes */
    const isFirstOfMedicationId =
      idx === 0 || allVersions[idx - 1]?.id !== curr.id

    const shouldInclude =
      isSignificantEvent ||
      isStatusChangeWithSubstantiveChanges ||
      isRenewalWithChanges ||
      (isFirstOfMedicationId && (idx === 0 || !prevVersion))

    if (shouldInclude) {
      acc.push(buildEvent(curr, eventType))
    }

    return acc
  }, [])

  /*
   * Deduplicate events by date + medication + dose + action
   *
   * BACKWARD COMPATIBILITY: Handle rapid version updates from Elation sync
   *
   * When medications are synced from Elation, the system may create multiple versions
   * within seconds of each other as it:
   * 1. Creates the initial MedicationRequest
   * 2. Adds missing extensions (orderType, signed-by, etc.)
   * 3. Runs background scripts to fix medication displays
   *
   * These rapid updates (within ~10 seconds) represent the SAME clinical order,
   * not separate orders. To avoid showing duplicates, we check:
   * - Same authoredOn date
   * - Same medication name (using medicationCodeableConcept.text for stability)
   * - Same Elation ID (from externalResourceJSON)
   *
   * For Elation-sourced medications, we prefer the MOST RECENT version (lower index)
   * because it has the most complete data (extensions populated, display fixed, etc.)
   *
   * Note: We use medicationCodeableConcept.text instead of medicationDisplay for
   * comparison because early versions may have placeholder reference strings in the
   * display field (e.g., "Medication/abc-123") which were later fixed by background
   * scripts. The medicationCodeableConcept.text field is more stable across versions.
   */
  const deduplicatedEvents = events.filter((event, idx, arr) => {
    const currentDate = event.medicationRequest.authoredOn
    const currentMedName = event.medicationRequest.medicationName
    const currentDose = extractDoseFromDisplay(currentMedName ?? '')
    const currentElationId = event.medicationRequest.elationId

    /* Check if there's an earlier event (lower index = more recent) with same clinical order */
    const hasDuplicate = arr.slice(0, idx).some((other) => {
      const otherDate = other.medicationRequest.authoredOn
      const otherMedName = other.medicationRequest.medicationName
      const otherDose = extractDoseFromDisplay(otherMedName ?? '')
      const otherElationId = other.medicationRequest.elationId

      /* For Elation orders, check Elation ID to detect rapid version updates of same order */
      if (
        currentElationId &&
        otherElationId &&
        currentElationId === otherElationId
      ) {
        const isDuplicate =
          currentDate === otherDate &&
          currentMedName === otherMedName &&
          currentDose === otherDose &&
          event.action === other.action

        return isDuplicate
      }

      /* For non-Elation orders, check action to distinguish different events */
      return (
        currentDate === otherDate &&
        currentMedName === otherMedName &&
        currentDose === otherDose &&
        event.action === other.action
      )
    })

    return !hasDuplicate
  })

  /* Add fulfillment system events for transmission status */
  const eventsWithFulfillment = addFulfillmentEvents(
    deduplicatedEvents,
    allVersions
  )

  /* Group events by MedicationRequest ID, showing latest at top level with related events nested */
  const groupedEvents = groupEventsByMedicationRequestId(
    eventsWithFulfillment,
    allVersions,
    showPharmacyStopAttribution
  )

  return groupedEvents
}

function nestedEventsForEntry(
  entry: MedicationEvent
): MedicationEvent['nestedEvents'] {
  return (
    entry.nestedEvents?.map((nested) => ({
      ...nested,
      timeFormat: getTimeFormat(nested.time),
    })) ?? []
  )
}

/*
 * Convert pharmacy transmission events into nested timeline entries so they
 * render as individual lines under the matching clinical action.
 */
function buildNestedTransmissionEvents(
  transmissionEvents: PharmacyTransmissionEvent[] | undefined
): { action: ReactNode; time?: string; timeFormat: 'date' | 'datetime' }[] {
  return orderBy(transmissionEvents ?? [], (event) => event.time ?? '', [
    'desc',
  ]).map((txEvent) => ({
    action: (
      <span
        className={cn(
          'text-xs',
          txEvent.isError ? 'text-destructive' : 'text-muted-foreground'
        )}
      >
        {txEvent.action}
        {txEvent.time && (
          <span className="ml-1 text-muted-foreground/60">
            (
            {formatDistanceToNow(parseISO(txEvent.time), {
              addSuffix: true,
            })}
            )
          </span>
        )}
      </span>
    ),
    time: txEvent.time,
    timeFormat: getTimeFormat(txEvent.time) as 'date' | 'datetime',
  }))
}

/*
 * Group events by MedicationRequest ID, showing the latest event at the top level
 * and related events (same MedicationRequest ID) as nested children.
 *
 * This creates a cleaner timeline where:
 * - Latest activity for each MedicationRequest appears at the top level
 * - Related activities (ordered → refilled → stopped) nest under the latest
 * - Transmission status icons appear on both top-level and nested events
 *
 * Example:
 *   [Top Level] Stopped Lipitor (most recent)
 *     └─ [Nested] Refilled Lipitor
 *     └─ [Nested] Ordered Lipitor
 */
function groupEventsByMedicationRequestId(
  events: MedicationEvent[],
  allVersions: ExtendedMedicationRequest[],
  showPharmacyStopAttribution: boolean
): MedicationEvent[] {
  /*
   * Group events by MedicationRequest ID.
   * This groups different versions of the same medication request together.
   */
  const eventsByMedReqId = new Map<string, MedicationEvent[]>()

  for (const event of events) {
    const medReqId = event.medicationRequest.id
    if (!medReqId) {
      /* Events without ID can't be grouped */
      continue
    }

    const existing = eventsByMedReqId.get(medReqId) ?? []
    existing.push(event)
    eventsByMedReqId.set(medReqId, existing)
  }

  /* Convert grouped events to nested structure */
  const result: MedicationEvent[] = []

  for (const [_, medReqEvents] of eventsByMedReqId.entries()) {
    if (medReqEvents.length === 1) {
      const singleEvent = medReqEvents[0]

      /* Single event - nest its pharmacy transmissions under the one timeline entry */
      const transmissionNested = buildNestedTransmissionEvents(
        singleEvent.transmissionEvents
      )
      result.push({
        ...singleEvent,
        nestedEvents:
          transmissionNested.length > 0 ? transmissionNested : undefined,
      })
      continue
    }

    /* Multiple events for same MedicationRequest - nest them */
    /* Sort events within this group by timestamp (newest first) */
    const sortedMedReqEvents = orderBy(
      medReqEvents,
      (event) =>
        event.timestamp ??
        event.medicationRequest.effectiveDate ??
        event.medicationRequest.lastUpdated,
      ['desc']
    )
    const [topLevelEvent, ...relatedEvents] = sortedMedReqEvents

    /* Convert related events to nested event format */
    const nestedEventsFromRelated = relatedEvents.map((relatedEvent) => {
      const patientId = isOfType(
        relatedEvent.medicationRequest.subject,
        'Patient'
      )
        ? (relatedEvent.medicationRequest.subject.reference?.split('/')[1] ??
          '')
        : ''

      const author = resolveMedicationActivityAuthor(
        relatedEvent,
        allVersions,
        showPharmacyStopAttribution
      )

      /* Build action with transmission status icon */
      const actionContent = (
        <span
          key={relatedEvent.key ?? relatedEvent.medicationRequest.versionId}
          className="inline-flex items-center gap-1"
        >
          {relatedEvent.actionBuilder
            ? relatedEvent.actionBuilder(
                relatedEvent.medicationRequest,
                patientId
              )
            : relatedEvent.action}
          {relatedEvent.transmissionEvents &&
            relatedEvent.transmissionEvents.length > 0 && (
              <PharmacyTransmissionStatusIcon
                events={relatedEvent.transmissionEvents}
              />
            )}
        </span>
      )

      return {
        author,
        action: actionContent,
        time:
          relatedEvent.timestamp ?? relatedEvent.medicationRequest.lastUpdated,
        children: relatedEvent.statusReason ? (
          <div className="text-xs text-muted-foreground">
            {relatedEvent.statusReason}
          </div>
        ) : undefined,
      }
    })

    /* Combine nested events: existing + related + transmission events as individual lines */
    const topLevelTransmissionNested = buildNestedTransmissionEvents(
      topLevelEvent.transmissionEvents
    )

    const combinedNestedEvents = orderBy(
      [
        ...(topLevelEvent.nestedEvents ?? []),
        ...nestedEventsFromRelated,
        ...topLevelTransmissionNested,
      ],
      (event) => event.time ?? '',
      ['desc']
    )

    result.push({
      ...topLevelEvent,
      nestedEvents:
        combinedNestedEvents.length > 0 ? combinedNestedEvents : undefined,
    })
  }

  /* Sort grouped events by timestamp descending (most recent first) */
  return orderBy(
    result,
    (event) =>
      event.timestamp ??
      event.medicationRequest.effectiveDate ??
      event.medicationRequest.lastUpdated,
    ['desc']
  )
}

/*
 * Extract fulfillment events from medication request versions and attach them
 * as inline status icons to their corresponding clinical actions
 *
 * Fulfillment events represent pharmacy transmission status:
 * - Successful sends (complete, sent)
 * - Failures (error, failure) with error messages
 * - Pending transmissions
 * - Cancellations
 *
 * These are "system" events without a human author, showing what happened
 * when the prescription was sent to the pharmacy via Surescripts.
 *
 * Strategy: Match fulfillment events to clinical actions by:
 * 1. MedicationRequest.id (which medication)
 * 2. Type (surescripts_cancel → stopped/cancelled action, else → ordered action)
 * 3. Temporal proximity (fulfillment happens shortly after clinical action)
 */
function addFulfillmentEvents(
  events: MedicationEvent[],
  allVersions: ExtendedMedicationRequest[]
): MedicationEvent[] {
  /* Track which fulfillment states we've already shown to avoid duplicates */
  const seenFulfillments = new Set<string>()

  /* Track previous state for each fulfillment type to detect error resolution */
  const previousStates = new Map<
    string,
    { state: string; timestamp: string; wasError: boolean }
  >()

  /* Sort versions chronologically to track state transitions */
  const sortedVersions = orderBy(
    allVersions,
    (version) => version.authoredOn || version.lastUpdated || '',
    ['asc']
  )

  /* Build a map of fulfillment events grouped by medication request ID and type */
  const fulfillmentsByMedication = new Map<
    string,
    {
      isCancellation: boolean
      events: PharmacyTransmissionEvent[]
    }[]
  >()

  for (const version of sortedVersions) {
    const externalData = version.externalResourceJSON
      ? (JSON.parse(version.externalResourceJSON) as {
          fulfillment?: {
            state?: string
            type?: string
            detail?: string
            time_completed?: string
          }
        })
      : null

    const fulfillment = externalData?.fulfillment
    if (!fulfillment?.state || !fulfillment?.type) {
      continue
    }

    /* Create unique key for this fulfillment to prevent duplicates.
     * Same fulfillment carried across multiple resource versions (e.g. after a
     * GraneRx update) will have identical type+state+detail and be deduped. */
    const fulfillmentKey = `${version.id}-${fulfillment.type}-${fulfillment.state}-${fulfillment.detail ?? ''}`
    if (seenFulfillments.has(fulfillmentKey)) {
      continue
    }
    seenFulfillments.add(fulfillmentKey)

    /* Determine the fulfillment action text and whether it's an error */
    const { action, isError } = getFulfillmentAction(fulfillment)

    /* Use time_completed if available, otherwise fall back to version lastUpdated */
    const fulfillmentDate =
      fulfillment.time_completed || version.lastUpdated || ''

    /* Check if this resolves a previous error */
    const typeKey = `${version.id}-${fulfillment.type}`
    const prevState = previousStates.get(typeKey)

    const fulfillmentEvents: PharmacyTransmissionEvent[] = []

    if (prevState?.wasError && !isError && fulfillment.state === 'complete') {
      /* Add a resolution event showing the error was resolved */
      fulfillmentEvents.push({
        action: 'Transmission issue resolved',
        time: fulfillmentDate,
        isError: false,
        source: 'elation',
      })
    }

    /* Update previous state for this type */
    previousStates.set(typeKey, {
      state: fulfillment.state,
      timestamp: fulfillmentDate,
      wasError: isError,
    })

    fulfillmentEvents.push({
      action,
      time: fulfillmentDate,
      isError,
      source: 'elation',
    })

    /* Group by medication request ID */
    if (!version.id) {
      continue
    }

    const isCancellation = fulfillment.type === 'surescripts_cancel'
    const existing = fulfillmentsByMedication.get(version.id) || []
    existing.push({ isCancellation, events: fulfillmentEvents })
    fulfillmentsByMedication.set(version.id, existing)
  }

  /* Second pass: extract GraneRx pharmacy reception events */
  for (const version of sortedVersions) {
    // Check pharmacyReception (new field) or legacy pharmacyTransmission with source='granerx'
    const reception =
      version.pharmacyReception ??
      (version.pharmacyTransmission?.source === 'granerx'
        ? version.pharmacyTransmission
        : undefined)
    if (!reception) {
      continue
    }

    if (!version.id) {
      continue
    }

    const receptionKey = `${version.id}-granerx-${reception.type}-${reception.state}`
    if (seenFulfillments.has(receptionKey)) {
      continue
    }
    seenFulfillments.add(receptionKey)

    const { action, isError } = getGraneRxTransmissionAction(reception)
    const receptionDate = reception.occurredAt || version.lastUpdated || ''

    const isCancellation =
      reception.type === 'discontinue' || reception.type === 'cancel'
    const existing = fulfillmentsByMedication.get(version.id) || []
    existing.push({
      isCancellation,
      events: [{ action, time: receptionDate, isError, source: 'granerx' }],
    })
    fulfillmentsByMedication.set(version.id, existing)
  }

  /* Third pass: reconciliation — downgrade Elation errors that predate GraneRx confirmation */
  for (const [, entries] of fulfillmentsByMedication) {
    // Find the latest GraneRx confirmation timestamp
    let latestGraneRxTime: string | undefined
    for (const entry of entries) {
      for (const ev of entry.events) {
        if (!ev.isError && ev.source === 'granerx') {
          if (!latestGraneRxTime || ev.time > latestGraneRxTime) {
            latestGraneRxTime = ev.time
          }
        }
      }
    }
    if (!latestGraneRxTime) {
      continue
    }
    // Only downgrade Elation errors that occurred BEFORE the GraneRx confirmation
    for (const entry of entries) {
      for (const event of entry.events) {
        if (
          event.isError &&
          event.source === 'elation' &&
          event.time &&
          event.time < latestGraneRxTime
        ) {
          event.isError = false
        }
      }
    }
  }

  /*
   * Check which fulfillment types have a matching clinical action.
   * If cancellation fulfillments have no stopped/cancelled event to attach to,
   * they should fall back to the order event (e.g., a cancel error on an active prescription).
   */
  const medReqsWithStopAction = new Set<string>()
  for (const event of events) {
    const id = event.medicationRequest.id
    if (id && (event.action === 'stopped' || event.action === 'cancelled')) {
      medReqsWithStopAction.add(id)
    }
  }

  /* Attach fulfillment events to their corresponding clinical actions as inline status icons */
  return events.map((event) => {
    const medReqId = event.medicationRequest.id
    if (!medReqId) {
      return event
    }

    const fulfillments = fulfillmentsByMedication.get(medReqId)
    if (!fulfillments || fulfillments.length === 0) {
      return event
    }

    /*
     * Match fulfillment events to clinical actions:
     * - Cancellation fulfillments (surescripts_cancel) → stopped/cancelled events
     * - Regular fulfillments → ordered/renewed/restarted events
     * - If no stop/cancel clinical event exists, cancellation fulfillments fall back to order events
     */
    const isClinicalStop =
      event.action === 'stopped' || event.action === 'cancelled'
    const isClinicalOrder =
      event.action === 'prescribed' ||
      event.action === 'refilled' ||
      event.action === 'restarted' ||
      event.action === 'renewed'

    const hasStopAction = medReqsWithStopAction.has(medReqId)

    const matchingFulfillments = fulfillments
      .filter((f) => {
        if (isClinicalStop) {
          return f.isCancellation
        }
        if (isClinicalOrder) {
          // Include non-cancellation fulfillments always.
          // Also include cancellation fulfillments if there's no stop/cancel
          // event to attach them to (fallback to order event).
          if (!f.isCancellation) {
            return true
          }
          return !hasStopAction
        }
        return false
      })
      .flatMap((f) => f.events)

    if (matchingFulfillments.length === 0) {
      return event
    }

    const transmissionEvents = uniqBy(matchingFulfillments, 'action')

    return {
      ...event,
      transmissionEvents: orderBy(
        transmissionEvents,
        (event) => event.time ?? '',
        ['desc']
      ),
    }
  })
}

interface GetFulfillmentActionResult {
  action: string
  isError: boolean
}

/*
 * Determine fulfillment action text based on state and type
 */
function getFulfillmentAction(fulfillment: {
  state?: string
  type?: string
  detail?: string
  time_completed?: string
}): GetFulfillmentActionResult {
  const { state, type, detail, time_completed } = fulfillment

  /* Handle error states */
  if (state === 'error' || state === 'failure') {
    const errorMsg = detail && detail !== '[000]' ? `: ${detail}` : ''
    return {
      action: `Prescription transmission failed${errorMsg}`,
      isError: true,
    }
  }

  /* Handle successful states */
  if (state === 'complete') {
    if (type === 'surescripts_cancel') {
      return {
        action: 'Prescription cancellation sent to pharmacy',
        isError: false,
      }
    }
    return {
      action: 'Prescription sent to pharmacy',
      isError: false,
    }
  }

  if (state === 'sent' || state === 'pending_complete') {
    /* Check if actually completed based on time_completed or success code */
    const hasCompletionIndicator = time_completed || detail === '[000]'

    if (type === 'surescripts_cancel') {
      return {
        action: hasCompletionIndicator
          ? 'Prescription cancellation sent to pharmacy'
          : 'Prescription cancellation pending',
        isError: false,
      }
    }
    return {
      action: hasCompletionIndicator
        ? 'Prescription sent to pharmacy'
        : 'Prescription transmission pending',
      isError: false,
    }
  }

  /* Fallback for unknown states */
  return {
    action: `Prescription ${state}`,
    isError: false,
  }
}

interface GetGraneRxTransmissionActionResult {
  action: string
  isError: boolean
}

interface TypeLabelsLookup {
  [key: string]: string
}

/*
 * Determine action text for GraneRx pharmacy transmission events.
 * GraneRx messages represent the pharmacy acknowledging an order,
 * not the initial electronic send (which comes from Elation/Surescripts).
 */
function getGraneRxTransmissionAction(transmission: {
  state: string
  type: string
  detail?: string
}): GetGraneRxTransmissionActionResult {
  const { state, type, detail } = transmission

  if (state === 'error' || state === 'failure') {
    const errorMsg = detail ? `: ${detail}` : ''
    return {
      action: `Prescription transmission failed${errorMsg}`,
      isError: true,
    }
  }

  const typeLabels: TypeLabelsLookup = {
    new_order: 'Prescription confirmed by pharmacy (GraneRx)',
    refill: 'Prescription refill confirmed by pharmacy (GraneRx)',
    change_order: 'Prescription change confirmed by pharmacy (GraneRx)',
    discontinue: 'Prescription discontinued at pharmacy (GraneRx)',
    cancel: 'Prescription cancelled at pharmacy (GraneRx)',
  }

  return {
    action: typeLabels[type] ?? `Pharmacy status update (GraneRx)`,
    isError: false,
  }
}
